import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Edit, Trash2, Tag, Ticket, Search,
  Check, Hash, Percent, DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CouponsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    code: "", description: "", discount_type: "percentage", discount_value: 10, max_uses: 1, is_active: true
  });

  // Redeem
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemClientCode, setRedeemClientCode] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } });
      setCoupons(res.data.coupons);
    } catch (e) { toast.error("Error al cargar cupones"); }
    finally { setIsLoading(false); }
  };

  const handleOpenCreate = () => {
    setEditingCoupon(null);
    setForm({ code: "", description: "", discount_type: "percentage", discount_value: 10, max_uses: 1, is_active: true });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (c) => {
    setEditingCoupon(c);
    setForm({ code: c.code, description: c.description || "", discount_type: c.discount_type, discount_value: c.discount_value, max_uses: c.max_uses, is_active: c.is_active });
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.description) { toast.error("Agrega una descripción"); return; }
    setIsSaving(true);
    try {
      if (editingCoupon) {
        await axios.put(`${API}/admin/coupons/${editingCoupon.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Cupón actualizado");
      } else {
        await axios.post(`${API}/admin/coupons`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Cupón creado");
      }
      setIsFormOpen(false);
      fetchCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este cupón?")) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Cupón eliminado");
      fetchCoupons();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) { toast.error("Ingresa el código del cupón"); return; }
    setIsRedeeming(true);
    try {
      const res = await axios.post(`${API}/admin/coupons/redeem`, {
        code: redeemCode.trim().toUpperCase(),
        client_code: redeemClientCode.trim().toUpperCase()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setIsRedeemOpen(false);
      setRedeemCode("");
      setRedeemClientCode("");
      fetchCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al canjear"); }
    finally { setIsRedeeming(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="coupons-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="coupons-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Admin
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Ticket className="w-5 h-5 text-purple-600" /> Cupones de Descuento
              </h1>
              <p className="text-xs text-stone-500">{coupons.length} cupones</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsRedeemOpen(true)} data-testid="redeem-coupon-btn">
              <Check className="w-4 h-4 mr-2" /> Canjear Cupón
            </Button>
            <Button onClick={handleOpenCreate} className="bg-purple-600 hover:bg-purple-700" data-testid="create-coupon-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Cupón
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {isLoading ? <div className="text-center py-12 text-stone-500">Cargando...</div> :
        coupons.length === 0 ? (
          <div className="text-center py-16">
            <Ticket className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500 text-lg mb-2">No hay cupones</p>
            <Button onClick={handleOpenCreate} variant="outline"><Plus className="w-4 h-4 mr-2" /> Crear primer cupón</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {coupons.map(c => (
              <Card key={c.id} className={`border-l-4 ${c.is_active ? 'border-l-purple-500' : 'border-l-stone-300 opacity-60'}`} data-testid={`coupon-card-${c.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <Badge className="bg-purple-100 text-purple-700 font-mono text-base mb-2" data-testid={`coupon-code-${c.code}`}>
                        <Tag className="w-3.5 h-3.5 mr-1" /> {c.code}
                      </Badge>
                      <p className="text-sm text-stone-600">{c.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(c)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(c.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        {c.discount_type === 'percentage'
                          ? <Percent className="w-5 h-5 text-purple-600" />
                          : <DollarSign className="w-5 h-5 text-purple-600" />
                        }
                      </div>
                      <div>
                        <p className="text-xl font-bold text-purple-700">
                          {c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`}
                        </p>
                        <p className="text-xs text-stone-500">descuento</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{c.times_used || 0} / {c.max_uses}</p>
                      <p className="text-xs text-stone-500">usos</p>
                      <Badge className={c.is_active ? 'bg-emerald-100 text-emerald-700 mt-1' : 'bg-stone-100 text-stone-500 mt-1'}>
                        {c.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ticket className="w-5 h-5" /> {editingCoupon ? "Editar Cupón" : "Nuevo Cupón"}</DialogTitle>
            <DialogDescription>{editingCoupon ? "Modifica los datos del cupón" : "Crea un nuevo cupón de descuento"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingCoupon && (
              <div className="space-y-2">
                <Label>Código (opcional, se genera automáticamente)</Label>
                <Input value={form.code} onChange={(e) => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="Ej: DESCUENTO20" className="font-mono" data-testid="coupon-form-code" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Ej: 10% descuento en próxima compra" data-testid="coupon-form-desc" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Descuento</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm(p => ({ ...p, discount_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Monto Fijo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor</Label>
                <Input type="number" value={form.discount_value} onChange={(e) => setForm(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))} min="0" data-testid="coupon-form-value" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Máximo de Usos</Label>
                <Input type="number" value={form.max_uses} onChange={(e) => setForm(p => ({ ...p, max_uses: parseInt(e.target.value) || 1 }))} min="1" data-testid="coupon-form-maxuses" />
              </div>
              <div className="flex items-center justify-between pt-7">
                <Label>Activo</Label>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-purple-600 hover:bg-purple-700" data-testid="coupon-save-btn">
              {isSaving ? "Guardando..." : (editingCoupon ? "Guardar" : "Crear Cupón")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem Dialog */}
      <Dialog open={isRedeemOpen} onOpenChange={setIsRedeemOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Check className="w-5 h-5 text-emerald-600" /> Canjear Cupón</DialogTitle>
            <DialogDescription>Introduce el código de cupón que el cliente te proporcionó</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Código del Cupón *</Label>
              <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="Ej: SALLY1234" className="font-mono text-lg text-center tracking-wider" data-testid="redeem-code-input" />
            </div>
            <div className="space-y-2">
              <Label>Código del Cliente (opcional)</Label>
              <Input value={redeemClientCode} onChange={(e) => setRedeemClientCode(e.target.value.toUpperCase())} placeholder="Ej: AB3K9F" className="font-mono text-center" maxLength={6} data-testid="redeem-client-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRedeemOpen(false)}>Cancelar</Button>
            <Button onClick={handleRedeem} disabled={isRedeeming} className="bg-emerald-600 hover:bg-emerald-700" data-testid="redeem-submit-btn">
              {isRedeeming ? "Canjeando..." : "Canjear Cupón"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponsPage;
